import axios from 'axios';

const API_BASE = '/api';

class TokenManager {
  getAccessToken() { return localStorage.getItem('accessToken'); }
  getRefreshToken() { return localStorage.getItem('refreshToken'); }
  getExpiresAt() { return parseInt(localStorage.getItem('tokenExpiresAt') || '0'); }

  setTokens(access, refresh, expiresIn) {
    localStorage.setItem('accessToken', access);
    if (refresh) localStorage.setItem('refreshToken', refresh);
    if (expiresIn) {
      // Store expiry time with 60 second buffer
      localStorage.setItem('tokenExpiresAt', String(Date.now() + (expiresIn - 60) * 1000));
    }
  }

  clearTokens() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiresAt');
    localStorage.removeItem('userEmail');
  }

  isAuthenticated() { return !!this.getAccessToken(); }

  isTokenExpired() {
    const expiresAt = this.getExpiresAt();
    if (!expiresAt) return false; // No expiry info, assume valid
    return Date.now() > expiresAt;
  }

  redirectToLogin() {
    this.clearTokens();
    // Avoid redirect loop
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
}

export const tokenManager = new TokenManager();

const api = axios.create({ baseURL: API_BASE, timeout: 900000 }); // 15 min timeout for large multi-file AI calls

// Before every request: check token expiry, try refresh if expired
api.interceptors.request.use(async (config) => {
  // Skip auth header for auth endpoints
  if (config.url?.includes('/auth/')) return config;

  if (tokenManager.isTokenExpired() && tokenManager.getRefreshToken()) {
    try {
      const res = await axios.post(`${API_BASE}/auth/refresh`, {
        refreshToken: tokenManager.getRefreshToken()
      });
      tokenManager.setTokens(res.data.accessToken, res.data.refreshToken, res.data.expiresOn);
    } catch {
      tokenManager.redirectToLogin();
      return Promise.reject(new Error('Session expired'));
    }
  }

  const token = tokenManager.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// After every response: handle 401/403 auth errors
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err.response?.status;
    const errorMsg = (err.response?.data?.detail || err.response?.data?.error || '').toLowerCase();

    // Token-related error detection
    const isAuthError = status === 401 ||
      (status === 403 && (errorMsg.includes('token') || errorMsg.includes('expired') || errorMsg.includes('authentication')));

    if (isAuthError && tokenManager.getRefreshToken() && !err.config._retry) {
      err.config._retry = true;
      try {
        const res = await axios.post(`${API_BASE}/auth/refresh`, {
          refreshToken: tokenManager.getRefreshToken()
        });
        tokenManager.setTokens(res.data.accessToken, res.data.refreshToken, res.data.expiresOn);
        err.config.headers.Authorization = `Bearer ${res.data.accessToken}`;
        return axios.request(err.config);
      } catch {
        tokenManager.redirectToLogin();
        return Promise.reject(err);
      }
    }

    if (isAuthError) {
      tokenManager.redirectToLogin();
      return Promise.reject(err);
    }

    return Promise.reject(err);
  }
);

export const authService = {
  getAuthUrl: async () => {
    const { data } = await api.get('/auth/login');
    return data;
  },
  handleCallback: async (code) => {
    try {
      const { data } = await api.post('/auth/callback', { code });
      tokenManager.setTokens(data.accessToken, data.refreshToken, data.expiresOn);
      if (data.email) localStorage.setItem('userEmail', data.email);
      return data;
    } catch (err) {
      if (err.response?.status === 403) throw { unauthorized: true, email: err.response.data.email };
      throw err;
    }
  },
  logout: () => {
    tokenManager.clearTokens();
    window.location.href = '/login';
  },
  isAuthenticated: () => tokenManager.isAuthenticated(),
};

export const chatService = {
  send: async (message, sessionId = null) => {
    const { data } = await api.post('/chat', { message, session_id: sessionId }, { timeout: 120000 });
    return data;
  },
};

export const proposalService = {
  upload: async (files) => {
    const form = new FormData();
    if (Array.isArray(files)) {
      files.forEach(f => form.append('files', f));
    } else {
      form.append('files', files);
    }
    const { data } = await api.post('/proposal/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data;
  },
  fetchFromSharePoint: async (sharepointUrls) => {
    const form = new FormData();
    const urls = Array.isArray(sharepointUrls) ? sharepointUrls : [sharepointUrls];
    form.append('sharepoint_urls', JSON.stringify(urls));
    const { data } = await api.post('/proposal/fetch-from-sharepoint', form);
    return data;
  },
  uploadToSharePoint: async (sessionId, siteUrl, folder) => {
    const form = new FormData();
    form.append('session_id', sessionId);
    form.append('site_url', siteUrl);
    form.append('folder', folder);
    const { data } = await api.post('/proposal/upload-to-sharepoint', form);
    return data;
  },
  analyze: async (sessionId) => {
    const form = new FormData();
    form.append('session_id', sessionId);
    const { data } = await api.post('/proposal/analyze', form, { timeout: 900000 });
    return data;
  },
  reSummarize: async (sessionId, edits) => {
    const form = new FormData();
    form.append('session_id', sessionId);
    form.append('human_edits', edits);
    const { data } = await api.post('/proposal/re-summarize', form, { timeout: 600000 });
    return data;
  },
  generate: async (sessionId, config, finalSummary) => {
    const form = new FormData();
    form.append('session_id', sessionId);
    form.append('config', JSON.stringify(config));
    form.append('final_summary', finalSummary);
    const { data } = await api.post('/proposal/generate', form, { timeout: 600000 });
    return data;
  },
  getDownloadUrl: (sessionId) => `/api/proposal/download-docx/${sessionId}`,
  getDrawioUrl: (sessionId) => `/api/proposal/download-drawio/${sessionId}`,
  uploadProposalToSP: async (sessionId, siteUrl, folder) => {
    const form = new FormData();
    form.append('site_url', siteUrl);
    form.append('folder', folder);
    const { data } = await api.post(`/proposal/upload-to-sharepoint/${sessionId}`, form);
    return data;
  },
};

export default api;
